/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import {
    CalendarDate,
    getDayOfWeek,
    getLocalTimeZone,
    isEqualDay,
    maxDate,
    minDate,
    parseAbsolute,
    toCalendarDate,
    toZoned,
} from '@internationalized/date';
import {
    addMonths,
    format as dateFnsFormat,
    formatISO,
    isWithinInterval,
    parse,
} from 'date-fns';
import isString from 'lodash/isString';
import toNumber from 'lodash/toNumber';
import { type DateValue } from 'react-aria';
import { calendarDateToUtcString, localNow, localToday } from 'sarsaparilla';

import { DEFAULT_TIMEZONE } from '../constants';
import { INVALID_DATE } from '../constants/dateFormats';

export const DAY_ENUM_FRIDAY = 5;
export const DAY_ENUM_SATURDAY = 6;
export const DAY_ENUM_SUNDAY = 0;

export function UTCStringToCalendarDate(utcString: string) {
    const dateWithTime = parseAbsolute(utcString, 'UTC');
    const calendarDate = toCalendarDate(dateWithTime);
    return calendarDate;
}

export function UTCStringToZonedDateTime(utcString: string) {
    return parseAbsolute(utcString, 'UTC');
}

export function CalendarDateToUTCString(calendarDate: CalendarDate) {
    return calendarDate.toDate(getLocalTimeZone()).toISOString();
}

/**
 * Serializes a CalendarDate to midnight UTC, which is how the reservation endpoints
 * expect dates (facilities are treated as if they were in UTC). Prefer this over
 * CalendarDateToUTCString for request payloads: that one applies the browser's zone
 * offset, which the modify and price endpoints reject as an invalid reservation.
 */
export function calendarDateToRequestDate(calendarDate: CalendarDate) {
    return toZoned(calendarDate, 'UTC').toAbsoluteString();
}

export const stringifyDate = (date: DateValue | string) => {
    if (!isString(date)) {
        return date.toString();
    }
    return date;
};

export const isBetweenDates = (
    date: DateValue | string,
    start: DateValue | string,
    end: DateValue | string
) => {
    return isWithinInterval(stringifyDate(date), {
        start: stringifyDate(start),
        end: stringifyDate(end),
    });
};

const format2Digit = (num: number) => {
    return num < 10 ? `0${num}` : num;
};

export const formatMDYShort = (date: DateValue, separator: string = '/') => {
    const { day, month, year } = date;

    return `${format2Digit(month)}${separator}${format2Digit(day)}${separator}${year}`;
};

export const formatMMM = (date: DateValue) => {
    const monthNames = [
        'Jan',
        'Feb',
        'Mar',
        'Apr',
        'May',
        'Jun',
        'Jul',
        'Aug',
        'Sep',
        'Oct',
        'Nov',
        'Dec',
    ];
    return `${monthNames[date.month - 1]}`;
};

export const searchDateToCalendarDate = (searchDate: string): CalendarDate => {
    const [month, day, year] = searchDate.split('/');
    return new CalendarDate(toNumber(year), toNumber(month), toNumber(day));
};

export const searchDateToUTCString = (searchDate: string): string => {
    return calendarDateToUtcString(searchDateToCalendarDate(searchDate)) as string;
};

export const isSameDay = (firstDate: DateValue, secondDate: DateValue) => {
    return isEqualDay(firstDate, secondDate);
};

export function formatAvailabilityAccessor(date: DateValue) {
    const dateStr = calendarDateToUtcString(date) as string;
    return dateStr.replace('.000Z', 'Z');
}
export const getTodayISO = (): string => {
    const utcDateString = toZoned(localToday, 'UTC').toAbsoluteString();
    return utcDateString;
};

export const isValidSimpleDate = (dateString: string) => {
    // match format MM-DD-YYYY or MM/DD/YYYY
    const dashRegex = /^\d{2}-\d{2}-\d{4}$/;
    const slashRegex = /^\d{2}\/\d{2}\/\d{4}$/;

    if (!dashRegex.test(dateString) && !slashRegex.test(dateString)) return false;

    const separator = dateString.includes('-') ? '-' : '/';
    const [month, day, year] = dateString.split(separator).map(Number);
    const daysInMonth = new Date(year, month, 0).getDate();

    return month >= 1 && month <= 12 && day >= 1 && day <= daysInMonth;
};

export const getDaysDifference = (date1: string, date2: string) => {
    // Convert dates to milliseconds
    const date1ms = new Date(date1).getTime();
    const date2ms = new Date(date2).getTime();

    // Calculate the difference in milliseconds
    const diffMs = Math.abs(date2ms - date1ms);

    // Convert milliseconds to days
    const millisecondsPerDay = 1000 * 60 * 60 * 24;
    const daysDiff = Math.round(diffMs / millisecondsPerDay);

    return daysDiff;
};

const getDateRange = (
    startDate: DateValue,
    endDate: DateValue,
    isStringValue: boolean
) => {
    const dates: any[] = [];
    let start = startDate;

    while (start.compare(endDate) <= 0) {
        const day = isStringValue ? calendarDateToUtcString(start) : start;
        if (day !== null) {
            dates.push(day);
        }
        start = start.add({ days: 1 });
    }
    return dates;
};

// Returns an array of dates in UTC string format from startDate to endDate inclusive
export const getDatesFromRange = (startDate: DateValue, endDate: DateValue): string[] => {
    return getDateRange(startDate, endDate, true);
};

export const getCalendarDatesFromRange = (
    startDate: DateValue,
    endDate: DateValue
): DateValue[] => {
    return getDateRange(startDate, endDate, false);
};

export function isDateSameOrAfter(date1: DateValue, date2: DateValue) {
    return date1.compare(date2) >= 0;
}

export function isDateSameOrBefore(date1: DateValue, date2: DateValue) {
    return date1.compare(date2) <= 0;
}
export function isDateAfter(date1: DateValue, date2: DateValue) {
    return date1.compare(date2) > 0;
}

export function isDateBefore(date1: DateValue, date2: DateValue) {
    return date1.compare(date2) < 0;
}

export function isDateBetweenExclusive(
    date: DateValue,
    start: DateValue,
    end: DateValue
) {
    return date.compare(start) > 0 && date.compare(end) < 0;
}

export function addReservationWindowRule(
    start: CalendarDate,
    windowValue: number,
    units: string
) {
    const lowerUnits = units.toLowerCase();

    if (lowerUnits.startsWith('month')) {
        return start.add({ months: windowValue });
    }

    if (lowerUnits.startsWith('day')) {
        return start.add({ days: windowValue });
    }

    if (lowerUnits.startsWith('week')) {
        return start.add({ weeks: windowValue });
    }

    if (lowerUnits.startsWith('year')) {
        return start.add({ years: windowValue });
    }

    return start.add({ months: windowValue });
}

export function formatDayFormat(
    date: DateValue,
    weekdayFormat: Intl.DateTimeFormatOptions['weekday'] = 'long'
) {
    return `${date.toDate('UTC').toLocaleDateString('us-en', { weekday: weekdayFormat, timeZone: 'UTC' })}, ${formatMMM(date)} ${date.day}, ${date.year}`;
}

export const checkInFlightFacilityTimeReservationStartDate = ({
    facilityTimeZone,
    reservationStart,
}: {
    facilityTimeZone: string;
    reservationStart: string;
}) => {
    const limitDate = toZoned(
        UTCStringToCalendarDate(reservationStart),
        facilityTimeZone || DEFAULT_TIMEZONE
    );

    return localNow.compare(limitDate) > 0;
};

export const getMinDate = (dates: CalendarDate[]): CalendarDate => {
    return dates.reduce(
        //@ts-ignore
        (minimumDate, currentDate) => minDate(minimumDate, currentDate),
        new CalendarDate(2050, 1, 1)
    );
};

export const getMaxDate = (dates: CalendarDate[]): CalendarDate => {
    return dates.reduce(
        //@ts-ignore
        (maximumDate, currentDate) => maxDate(maximumDate, currentDate),
        new CalendarDate(1920, 1, 1)
    );
};

export const isDateValid = (date: string) => {
    return date && date !== INVALID_DATE;
};

export const isValidUrlDate = (date: string) => {
    // match format MM-DD-YYYY
    const regex = /^\d{2}\/\d{2}\/\d{4}$/;
    return regex.test(date);
};

export const formatUTCDateFnsDate = (date: string, formatStr: string) => {
    return dateFnsFormat(
        UTCStringToCalendarDate(date).toDate(getLocalTimeZone()),
        formatStr
    );
};

export const formatUTCToTimeZone = (
    utcString: string,
    timezone: string,
    formatStr: string
) => {
    const utcDateTime = parseAbsolute(utcString, 'UTC');
    const zonedDateTime = toZoned(utcDateTime, timezone || DEFAULT_TIMEZONE);

    // Create a date object with the zoned components to avoid timezone conversion
    const { year, month, day, hour, minute, second } = zonedDateTime;
    const timeZoneDate = new Date(year, month - 1, day, hour, minute, second);

    return dateFnsFormat(timeZoneDate, formatStr);
};

export const format = (
    date: DateValue | null | undefined,
    formatStr: string,
    fallback?: string
) => {
    // We can add a validator here if we want
    if (!date) {
        return fallback || '';
    }
    return dateFnsFormat(date.toDate(getLocalTimeZone()), formatStr);
};

export const parseTimeString = (timeString: string): Date => {
    if (!timeString) return new Date();

    const parsed24 = parse(timeString, 'HH:mm', new Date());
    if (!Number.isNaN(parsed24.getTime())) return parsed24;

    const parsed12 = parse(timeString, 'h:mm a', new Date());
    if (!Number.isNaN(parsed12.getTime())) return parsed12;

    return new Date();
};

export const formatToUtcIfValid = (dateStr: string, formatStr: string) => {
    if (isDateValid(dateStr)) {
        return formatUTCDateFnsDate(dateStr, formatStr);
    }

    return null;
};

export const calendarDateToSearchDate = (date: DateValue): string => {
    return format(date, 'MM/dd/yyyy');
};

export const isWeekendDay = (date: CalendarDate) => {
    const dayOfWeek = getDayOfWeek(date, 'en-US');
    return dayOfWeek === DAY_ENUM_FRIDAY || dayOfWeek === DAY_ENUM_SATURDAY;
};

export const isDayUseWeekendDay = (date: CalendarDate) => {
    const dayOfWeek = getDayOfWeek(date, 'en-US');
    return dayOfWeek === DAY_ENUM_SUNDAY || dayOfWeek === DAY_ENUM_SATURDAY;
};

/**
 * Check if two date ranges overlap (including adjacent dates)
 * @param {DateValue} start1 - Start date of first range
 * @param {DateValue} end1 - End date of first range
 * @param {DateValue} start2 - Start date of second range
 * @param {DateValue} end2 - End date of second range
 * @returns {boolean} - True if ranges overlap or are adjacent
 */
export const doDateRangesOverlap = (
    start1: DateValue,
    end1: DateValue,
    start2: DateValue,
    end2: DateValue
): boolean => {
    return isDateSameOrBefore(start1, end2) && isDateSameOrAfter(end1, start2);
};

export const getDateDiffInMilliseconds = (
    endDate: DateValue,
    startDate: DateValue
): number => {
    const endMs = endDate.toDate('UTC').getTime();
    const startMs = startDate.toDate('UTC').getTime();
    return endMs - startMs;
};

export const convertIntegerToOrdinal = (num: number) => {
    const s = ['th', 'st', 'nd', 'rd'];
    const v = num % 100;
    return num + (s[(v - 20) % 10] || s[v] || s[0]);
};

/**
 * Builds an array of avail_months strings for the campsite search API:
 * one per month starting from startingMonth, in YYYY-MM-DDTHH:mm:ssZ form.
 */
export function getAvailMonthsFromStartingMonth(
    startingMonth: Date,
    monthsToSearch: number
): string[] {
    return Array.from(
        { length: monthsToSearch },
        (_, i) =>
            `${formatISO(addMonths(startingMonth, i), { representation: 'date' })}T00:00:00Z`
    );
}
